/**
 * 发送聊天消息的原子
 * 处理消息发送的核心逻辑，包括输入清空、错误处理等
 */
import { atom } from 'jotai';
import { createStandaloneToast } from '@chakra-ui/react';
import i18n from '../../lib/i18n';
import { chatInputAtom } from './chatInputAtoms';
import { selectedCharacterIdAtom } from './characterAtoms';
import { messageSendingAtom, selectedConversationIdAtom } from './chatAtoms';
import { sendStreamingMessageAtom } from './streaming/messageStreaming';
import { cleanupUppyAtom, fileValuesSelector } from './uppyAtoms';
import { MessageContent } from '../../types';
import { shouldAutoScrollAtom } from './scrollControlAtoms';
import { startResponseTimerAtom } from './responseTimeAtoms';


// 创建独立的 toast 实例
const { toast } = createStandaloneToast();

/**
 * 发送聊天消息的原子
 * 处理消息发送的核心逻辑
 */
export const triggerChatMessageAtom = atom(
  null,
  async (get, set) => {
    // 开始响应时间计时 - 无论通过哪种方式发送都计时
    set(startResponseTimerAtom);

    // 获取输入内容和角色ID
    const chatInput = get(chatInputAtom);
    const selectedCharacterId = get(selectedCharacterIdAtom);
    const fileValues = get(fileValuesSelector);

    const content: MessageContent[] = [];

    // Add Text Block
    content.push({ type: 'text', text: chatInput.trim() });

    // Add image & document Blocks
    fileValues.forEach((file) => {
      if (file.type.startsWith('image/')) {
        content.push({
          type: 'image',
          source: {
            type: 'url',
            url: file.finalUrl || ''
          }
        });
      } else {
        content.push({
          type: 'document',
          source: {
            type: 'url',
            url: file.finalUrl || ''
          }
        });
      }
    });

    console.log('INPUT CONTENT:', content)

    // 验证输入
    if (!chatInput.trim()) return null;

    // 清空输入框
    set(chatInputAtom, '');

    // 清空文件上传
    set(cleanupUppyAtom)

    // 重置自动滚动状态，确保发送消息后自动滚动到底部
    set(shouldAutoScrollAtom, true);

    try {
      // 使用流式聊天发送消息
      const actualConversationId = await set(sendStreamingMessageAtom, {
        content,
        characterId: selectedCharacterId,
      });

      // 如果成功，调用成功回调
      if (actualConversationId) {
        // console.log(`selectedConversationIdAtom: ${selectedConversationIdAtom}`)
        // set(navigateAtom, { path: `/v3/chat-detail/${actualConversationId}`, replace: true })
        set(selectedConversationIdAtom, actualConversationId);
      } else if (!actualConversationId) {
        console.error("Message send failed (handled by atom).");
      }


      return actualConversationId;
    } catch (err) {
      console.error('Error initiating message send:', err);

      // 显示错误提示
      toast({
        title: i18n.t('v3/chat:messages.sendFailed', '发送失败'),
        description: err instanceof Error ? err.message : i18n.t('common:errors.unknown', '未知错误'),
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top'
      });
      return null;
    } finally {
      set(messageSendingAtom, false);
    }
  }
);

export default triggerChatMessageAtom;
